import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  LayoutDashboard,
  MessageCircle,
  BarChart2,
  Settings,
  Video,
  FileText,
  Database,
  Users,
  Award,
  ChevronDown,
  ArrowRightToLine,
  ArrowLeftToLine,
  Target,
  Bot,
  UserCog,
  DollarSign,
  Heart,
  CircleDot,
  Bell,
  BarChart3,
  Shield,
  CloudUpload,
} from "lucide-react";
import axios from "axios";

import { Link, useLocation, useNavigate } from "react-router-dom";
import { getSelectedTeam, isNorthCarolinaTeam } from "../utils/helpers";
import { useTeam } from "../lib/teamConfig";
import { getSidebarPreferences } from "../utils/sidebarPreferences";
// Shared design tokens for unified sidebar styling
import { sidebarStyles } from "../tokens/sidebar-styles";

// Import centralized labels and role configuration
import {
  NAVIGATION_LABELS,
  ROUTE_MAPPINGS,
  getVisibleItemsForRole,
} from "../constants/userFacingLabels";
import {
  ROLE_TYPES,
  ROLE_HIERARCHY,
  canRoleAccessRoute,
  migrateRoleName,
} from "../config/roleConfig";

const Sidebar = ({
  activeView,
  setActiveView,
  isSidebarOpen,
  showLogo,
  setShowLogo,
  setIsSidebarOpen,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Sport/Team context for sport toggle
  const { selectedSport, selectSport, hasSportSelected, config } = useTeam();

  // Use set of expanded sections instead of single value
  // Default to Player Development, Roster Management, and System expanded on login
  const [expandedSections, setExpandedSections] = useState(
    new Set(['v3', 'playerDevelopment', 'rosterManagement', 'system'])
  );

  // User role and accessible routes from session
  const [userRole, setUserRole] = useState(null);
  const [accessibleRoutes, setAccessibleRoutes] = useState([]);

  // Pending approval count for admin badge
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);

  // Sidebar customization preferences (from localStorage)
  const [sidebarPrefs, setSidebarPrefs] = useState(() => getSidebarPreferences());

  // Get the effective role for the user (migrate old roles if needed)
  const getEffectiveRole = () => {
    if (!userRole) return null;
    if (!ROLE_HIERARCHY[userRole]) {
      return migrateRoleName(userRole);
    }
    return userRole;
  };

  const effectiveRole = getEffectiveRole();

  // Group navigation items - Reorganized for coach-friendly workflow
  // Priority: Daily tasks first, advanced features in dedicated sections
  // Using centralized labels from userFacingLabels.js
  // Updated January 2026: Restructured per Demo Community feedback
  const navSections = [
    // ═══════════════════════════════════════════════════════════════════════════
    // GENERAL MANAGER - GM dashboard, cap sheet, compliance
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: "generalManager",
      label: "GENERAL MANAGER",
      items: [
        {
          id: "capSheet",
          label: "Cap Sheet",
          description: "NIL cap allocation and roster financial planning",
          icon: <DollarSign size={20} />,
          path: `/${selectedSport || 'basketball'}/gm/cap-sheet`,
        },
        {
          id: "compliance",
          label: "Compliance",
          description: "NCAA compliance tracking and audit tools",
          icon: <Shield size={20} />,
          path: `/${selectedSport || 'basketball'}/gm/compliance`,
        },
      ],
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // PLAYER DEVELOPMENT - Player performance, health, and training
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: "playerDevelopment",
      label: "PLAYER DEVELOPMENT",
      items: [
        {
          id: "videoAnalytics",
          label: "Film Room",
          description: "Cut · Reel · Vault — biomechanics, session film, bulk archive",
          icon: <Video size={20} />,
          path: "/video-analytics",
        },
        {
          id: "gameFilm",
          label: "Game Film",
          description: "Video analysis and game film",
          icon: <Video size={20} />,
          path: "/basketball/game-film",
        },
        {
          id: "dropZone",
          label: "Drop Zone",
          description: "Upload game film for AI-powered 6-lens analysis pipeline",
          icon: <CloudUpload size={20} />,
          path: "/basketball/drop-zone",
          highlight: true,
        },
      ],
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // ROSTER MANAGEMENT - Team roster and player development
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: "rosterManagement",
      label: "ROSTER MANAGEMENT",
      items: [
        {
          id: "myTeam",
          label: NAVIGATION_LABELS.MY_TEAM.label, // "Roster Management"
          description: NAVIGATION_LABELS.MY_TEAM.description,
          icon: <Users size={20} />,
          path: ROUTE_MAPPINGS.MY_TEAM.current, // "/my-team"
        },
      ],
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // SCOUTING - AI-powered scouting and analysis
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: "scouting",
      label: "SCOUTING",
      items: [
        {
          id: "askSlamAI",
          label: "Ask SLAM AI",
          description: "Chat with AI to analyze players, generate reports, upload files, and more",
          icon: <MessageCircle size={20} />,
          path: "/basketball/ask-slam-ai",
          highlight: true, // Special flag for prominent styling
        },
      ],
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // PLAYER ACQUISITION - Talent acquisition (Transfer Portal, Free Agents, Trades)
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: "playerAcquisition",
      label: "PLAYER ACQUISITION",
      items: [
        {
          id: "nilDashboard",
          label: "NIL Dashboard",
          description: "Track NIL valuations, deals, and market opportunities",
          icon: <DollarSign size={20} />,
          path: "/nil-dashboard",
        },
        {
          id: "tradeTargets",
          label: "Trade Targets",
          description: "Identify and evaluate potential trade targets",
          icon: <Award size={20} />,
          path: "/trade-targets",
        },
      ],
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // GETTING STARTED - Onboarding and dashboard
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: "gettingStarted",
      label: "GETTING STARTED",
      items: [
        {
          id: "theWire",
          label: "The Wire",
          description: "AI-powered intelligence and insights hub",
          icon: <Bot size={20} />,
          path: "/baseball/the-wire",
        },
      ],
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // V3 — Next-gen AI-powered features
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: "v3",
      label: "V3",
      items: [
        {
          id: "gmDashboard",
          label: "GM Dashboard",
          description: "Roster analytics, cap management, and compliance overview",
          icon: <BarChart3 size={20} />,
          path: `/${selectedSport || 'basketball'}/gm`,
        },
        {
          id: "canvas",
          label: "Agent Builder",
          description: "Build and configure AI agents with three pillars",
          icon: <Bot size={20} />,
          path: `/${selectedSport || 'basketball'}/canvas`,
        },
      ],
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // SYSTEM - Settings, Admin & Configuration
    // ═══════════════════════════════════════════════════════════════════════════
    {
      id: "system",
      label: "SYSTEM",
      items: [
        {
          id: "settings",
          label: NAVIGATION_LABELS.SETTINGS.label, // "Settings"
          description: NAVIGATION_LABELS.SETTINGS.description,
          icon: <Settings size={20} />,
          path: ROUTE_MAPPINGS.SETTINGS.current, // "/settings"
        },
        // Admin-only: User Management (formerly Permissions)
        ...(effectiveRole === ROLE_TYPES.ADMIN
          ? [
              {
                id: "userManagement",
                label: NAVIGATION_LABELS.USER_MANAGEMENT.label, // "User Management"
                description: NAVIGATION_LABELS.USER_MANAGEMENT.description,
                icon: <UserCog size={20} />,
                path: ROUTE_MAPPINGS.USER_MANAGEMENT.current, // "/user-management"
              },
            ]
          : []),
        {
          id: "reports",
          label: NAVIGATION_LABELS.REPORTS.label, // "Reports"
          description: NAVIGATION_LABELS.REPORTS.description,
          icon: <FileText size={20} />,
          path: ROUTE_MAPPINGS.REPORTS.current, // "/reports"
        },
        {
          id: "integrations",
          label: NAVIGATION_LABELS.INTEGRATIONS.label, // "Integrations"
          description: NAVIGATION_LABELS.INTEGRATIONS.description,
          icon: <Database size={20} />,
          path: ROUTE_MAPPINGS.INTEGRATIONS.current, // "/integrations"
        },
      ],
    },
  ];

  // Routes that are always visible to authenticated users (bypass accessibleRoutes check)
  // These are the new AI-powered features available to all users
  // Updated January 2026: Re-added THE WIRE, added new scouting routes
  const alwaysVisibleRoutes = [
    "/slam-ai",             // SLAM AI - legacy redirect
    "/basketball/ask-slam-ai", // SLAM AI - basketball
    "/basketball/canvas",   // Agent Builder - basketball
    `/${selectedSport || 'basketball'}/canvas`,   // Agent Builder - current sport
    `/${selectedSport || 'basketball'}/gm`,       // GM Dashboard - current sport
    `/${selectedSport || 'basketball'}/gm/cap-sheet`, // Cap Sheet - current sport
    `/${selectedSport || 'basketball'}/gm/compliance`, // Compliance - current sport
    "/ai-video-analyzer",   // Gemini MoE video analysis
    "/video-analyzer",      // Legacy path - video analysis
    "/video-analytics",     // Film Room - Cut/Reel/Vault tier selector
    "/basketball/drop-zone", // Drop Zone - basketball
    "/development",         // Player development plans
    "/nil-dashboard",       // Free Agent Dashboard (renamed from NIL Dashboard)
    "/trade-targets",       // Trade Targets - evaluate potential trades
    "/the-wire",            // The Wire - AI-powered intelligence hub
    "/baseball/the-wire",   // The Wire - sport-specific
  ];

  // Filter navigation sections based on:
  // 1. User's accessible routes AND role-based access
  // 2. User's sidebar preferences (hidden items, section order)
  const filteredNavSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        // Skip items without paths (roadmap/placeholder items)
        if (!item.path) return false;

        // Skip items hidden by user preferences
        if (sidebarPrefs.hiddenItems.includes(item.id)) return false;

        // For User Management route, check if user is admin
        if (item.path === "/user-management" || item.path === "/permissions") {
          return effectiveRole === ROLE_TYPES.ADMIN;
        }

        // V2 AI Features are always visible to authenticated users
        if (alwaysVisibleRoutes.includes(item.path)) {
          return true;
        }

        // Check role-based access (new system)
        if (effectiveRole && canRoleAccessRoute(effectiveRole, item.path)) {
          return true;
        }

        // Fallback to legacy accessibleRoutes array
        return accessibleRoutes.includes(item.path);
      }),
    }))
    .filter((section) => section.items.length > 0) // Remove empty sections
    // Sort sections by user preference order
    .sort((a, b) => {
      const orderA = sidebarPrefs.sectionOrder.indexOf(a.id);
      const orderB = sidebarPrefs.sectionOrder.indexOf(b.id);
      // If section not in order list, put it at the end
      const posA = orderA === -1 ? 999 : orderA;
      const posB = orderB === -1 ? 999 : orderB;
      return posA - posB;
    });

  // Flatten nav items for checking active state (use filtered items)
  const allNavItems = filteredNavSections.flatMap((section) => section.items);

  // Get user role and accessible routes from session storage
  useEffect(() => {
    const getUserData = () => {
      try {
        const userData = sessionStorage.getItem("user");
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setUserRole(parsedUser.role);
          setAccessibleRoutes([...parsedUser.accessibleRoutes] || []);
        }
      } catch (error) {
        console.error("Error parsing user data from session storage:", error);
      }
    };

    getUserData();

    // Listen for storage events to update data if changed in another tab
    window.addEventListener("storage", getUserData);
    return () => {
      window.removeEventListener("storage", getUserData);
    };
  }, []);

  // Fetch pending approval count for admin users
  useEffect(() => {
    const fetchPendingCount = async () => {
      // Only fetch for admin users
      if (effectiveRole !== ROLE_TYPES.ADMIN) {
        setPendingApprovalCount(0);
        return;
      }

      try {
        const userData = sessionStorage.getItem("user");
        if (!userData) return;

        const user = JSON.parse(userData);
        const token = user?.token;

        const response = await axios.get('/api/users/pending-count', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (response.data?.success) {
          setPendingApprovalCount(response.data.pendingCount || 0);
        }
      } catch (error) {
        console.error('Error fetching pending approval count:', error);
        // Don't show error to user, just don't show the badge
      }
    };

    fetchPendingCount();

    // Refresh every 60 seconds for admin users
    const interval = effectiveRole === ROLE_TYPES.ADMIN
      ? setInterval(fetchPendingCount, 60000)
      : null;

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [effectiveRole]);

  // Listen for sidebar preference changes (from Settings page)
  useEffect(() => {
    const handlePrefsChange = () => {
      setSidebarPrefs(getSidebarPreferences());
    };

    window.addEventListener("sidebarPreferencesChanged", handlePrefsChange);
    return () => {
      window.removeEventListener("sidebarPreferencesChanged", handlePrefsChange);
    };
  }, []);

  // Find which section contains the active item and expand it
  useEffect(() => {
    if (isCollapsed) return;

    const activeItem = allNavItems.find(
      (item) => item.id === activeView || item.path === location.pathname
    );

    if (activeItem) {
      const activeSection = filteredNavSections.find((section) =>
        section.items.some((item) => item.id === activeItem.id)
      );

      if (activeSection) {
        // Add to set of expanded sections instead of replacing
        setExpandedSections((prev) => {
          const newSet = new Set(prev);
          newSet.add(activeSection.id);
          return newSet;
        });
      }
    }
  }, [activeView, location.pathname, isCollapsed]);

  // Save expanded sections to localStorage
  useEffect(() => {
    if (!isCollapsed) {
      localStorage.setItem(
        "expandedSections",
        JSON.stringify(Array.from(expandedSections))
      );
    }
  }, [expandedSections, isCollapsed]);

  // Load expanded sections from localStorage
  // Default to both playerDevelopment and rosterManagement if nothing saved or empty
  useEffect(() => {
    const savedSections = localStorage.getItem("expandedSections");
    if (savedSections) {
      try {
        const parsed = JSON.parse(savedSections);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Migrate from old 'main' section ID to 'playerDevelopment'
          const sectionsSet = new Set(parsed);

          // Remove old 'main' section and replace with 'playerDevelopment'
          if (sectionsSet.has('main')) {
            sectionsSet.delete('main');
            sectionsSet.add('playerDevelopment');
          }

          // Ensure playerDevelopment is always included (one-time migration for existing users)
          if (!sectionsSet.has('playerDevelopment')) {
            sectionsSet.add('playerDevelopment');
          }

          // Save the updated set back to localStorage
          localStorage.setItem("expandedSections", JSON.stringify(Array.from(sectionsSet)));
          setExpandedSections(sectionsSet);
        } else {
          // Default to both sections if saved array is empty
          setExpandedSections(new Set(['playerDevelopment', 'rosterManagement']));
        }
      } catch (e) {
        console.error("Error parsing saved sections", e);
        setExpandedSections(new Set(['playerDevelopment', 'rosterManagement']));
      }
    }
    // If nothing saved, keep the default from initial state
  }, []);

  // Load collapsed state from localStorage
  useEffect(() => {
    const savedCollapsedState = localStorage.getItem("sidebarCollapsed");
    if (savedCollapsedState !== null) {
      setIsCollapsed(savedCollapsedState === "true");
    }
  }, []);

  // Close sidebar on mobile when clicking a nav item
  const handleNavClick = (itemId) => {
    setActiveView(itemId);
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);

    // Always ensure sidebar is visible when toggling on any screen size
    setIsSidebarOpen(true);

    // Save collapsed state to localStorage
    localStorage.setItem(
      "sidebarCollapsed",
      newCollapsedState ? "true" : "false"
    );
  };

  // Close sidebar when clicking outside on mobile
  const handleOverlayClick = () => {
    setIsSidebarOpen(false);
  };

  // Determine if NC team logo should render
  const shouldRenderLogo = useMemo(() => {
    return isNorthCarolinaTeam();
  }, [location?.pathname]);

  // Reset showLogo state when component mounts or location changes
  useEffect(() => {
    if (setShowLogo) {
      setShowLogo(false);
    }
  }, [setShowLogo, location?.pathname]);

  return (
    <>
      {/* Overlay backdrop for mobile */}
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 md:hidden ${
          isSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleOverlayClick}
      />

      {/* Sidebar */}
      <div
        className={`fixed md:relative ${
          isCollapsed ? "w-[56px]" : "w-[200px]"
        } h-full transform transition-all duration-300 ease-in-out will-change-transform ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        } z-20 flex flex-col bg-black text-white border-r border-[#1A1A1A]`}
      >
        {/* Sport Toggle removed - now in Header via SportToggle component */}

        {/* Navigation - scrollable with padding for absolute footer */}
        <nav
          className="flex-1 py-4 overflow-y-auto min-h-0 pb-16"
        >
          {filteredNavSections.map((section) => (
            <div key={section.id} className="mb-2">
              {!isCollapsed && (
                <div
                  className={sidebarStyles.sectionHeader()}
                  onClick={() => toggleSection(section.id)}
                >
                  <span className="text-left">{section.label}</span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${
                      expandedSections.has(section.id) ? "rotate-180" : ""
                    }`}
                  />
                </div>
              )}

              {/* Display section items if collapsed or if this section is expanded */}
              {(isCollapsed || expandedSections.has(section.id)) && (
                <ul className={`${isCollapsed ? "mt-1" : "ml-0"}`}>
                  {section.items.map((item) => {
                    const isActive =
                      activeView === item.id ||
                      location.pathname === item.path ||
                      (item.id === "playerRoster" &&
                        (location.pathname.startsWith("/player-roster") ||
                          location.pathname.startsWith("/player")));
                    return (
                      <li key={item.id} className="mb-1">
                        <Link
                          to={item.path}
                          onClick={() => {
                            if (item?.path) {
                              handleNavClick(item.id);
                            }
                          }}
                          className={sidebarStyles.navItem(true, isActive, isCollapsed, item.highlight)}
                          title={isCollapsed ? item.label : (item.description || "")}
                        >
                          <div
                            className={`flex-shrink-0 ${
                              isActive ? "text-[#E8B800]" : ""
                            } transform transition-transform duration-200 group-hover:scale-110`}
                          >
                            {React.cloneElement(item.icon, {
                              className: sidebarStyles.icon(true, isActive),
                            })}
                          </div>
                          {!isCollapsed && (
                            <div className="flex items-center justify-between flex-1">
                              <span
                                className={`text-left font-medium ${
                                  isActive ? "text-[#E8B800]" : "text-[#999999]"
                                } transition-all duration-200`}
                              >
                                {item.label}
                              </span>
                              {/* Pending approval badge for User Management */}
                              {item.id === "userManagement" && pendingApprovalCount > 0 && (
                                <span
                                  className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-red-500/80 text-white animate-pulse"
                                  title={`${pendingApprovalCount} user(s) pending approval`}
                                >
                                  {pendingApprovalCount}
                                </span>
                              )}
                            </div>
                          )}
                          {/* Show badge on collapsed sidebar icon */}
                          {isCollapsed && item.id === "userManagement" && pendingApprovalCount > 0 && (
                            <span
                              className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center text-[10px] font-bold rounded-full bg-red-500 text-white animate-pulse"
                              title={`${pendingApprovalCount} user(s) pending approval`}
                            >
                              {pendingApprovalCount > 9 ? "9+" : pendingApprovalCount}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </nav>
        {/* Footer */}
        <div
          className={`absolute bottom-0 left-0 ${
            isCollapsed ? "w-[56px]" : "w-[200px]"
          } px-3 py-2 transition-all duration-300 bg-black border-t border-[#1A1A1A]`}
        >
          {/* Basketball Link */}
          <Link
            to="/basketball"
            className="flex items-center gap-3 px-3 py-2 rounded-[10px] mb-2 transition-all duration-200 text-[#E8B800] hover:bg-[rgba(232,184,0,0.08)]"
            title="SLAM Basketball"
          >
            <CircleDot size={18} />
            {!isCollapsed && (
              <span className="text-sm font-medium">SLAM Basketball</span>
            )}
          </Link>
          {/* Action buttons */}
          <div
            className={`flex items-center ${
              isCollapsed ? "justify-start" : "justify-end"
            } `}
          >
            <button
              onClick={toggleCollapse}
              className="p-2.5 rounded-[10px] transition-all duration-200 hover:bg-[rgba(255,255,255,0.04)] text-[#999999] hover:text-[#F5F5F5]"
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <ArrowRightToLine size={16} />
              ) : (
                <ArrowLeftToLine size={16} />
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
